import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFeatureToggle } from '@flopflip/react-redux';
import { getMaxLengthError } from '@lender/utils';
import _ from 'lodash';
import { setLocale } from 'yup';

import { FeatureFlag } from 'constants/featureFlags';
import { omitEmptyValues } from 'utils/data';
import { normalizeFormPath } from 'utils/form';
import { logger } from 'utils/logger';

import { useEffectSafeStrictMode } from './useEffectSafeStrictMode';

setLocale({
  string: {
    max: getMaxLengthError('${max}'),
  },
  number: {
    min: ({ min }) => `Field value must be greater than or equal to ${min}`,
  },
});

const useValidation = formRef => {
  const [validationErrors, setValidationErrors] = useState({});
  const schema = useRef(null);
  const excludedValidationPathsRef = useRef(new Set());
  const validationErrorsRef = useRef({});
  const pendingValidationRef = useRef(null);

  const setExcludedValidationPaths = useCallback(paths => {
    excludedValidationPathsRef.current = new Set([...paths].map(normalizeFormPath));
  }, []);

  const isOmittedFalsyValidationErrorsDisabled = useFeatureToggle(
    FeatureFlag.DisableOmittedFalsyValidationErrors,
  );

  const omittedValidationErrors = useMemo(() => {
    if (isOmittedFalsyValidationErrorsDisabled) {
      return validationErrors;
    }

    return omitEmptyValues(validationErrors);
  }, [isOmittedFalsyValidationErrorsDisabled, validationErrors]);

  const getFieldsWithPotentialErrors = useCallback(
    async (prefix, formSchema = schema.current) => {
      if (!formSchema) {
        return [];
      }

      if (formSchema.type === 'array') {
        const acc = [];
        const value = _.get(formRef.current, prefix);
        const arrayLength = _.size(value);
        const innerSchema = formSchema.innerType || formSchema._subType;

        if (!_.isArray(value) || !innerSchema || !arrayLength) {
          return acc;
        }

        for (let i = 0; i < arrayLength; i += 1) {
          const path = prefix ? `${prefix}[${i}]` : `[${i}]`;
          const nestedFields = await getFieldsWithPotentialErrors(path, innerSchema ?? null);

          acc.push(...nestedFields);
        }

        return acc;
      }

      const schemaNestedFields = formSchema.fields;

      if (schemaNestedFields) {
        const acc = [];

        for (const [key, subSchema] of Object.entries(schemaNestedFields)) {
          const path = prefix ? `${prefix}.${key}` : key;
          const nestedFields = await getFieldsWithPotentialErrors(path, subSchema ?? null);

          acc.push(...nestedFields);
        }

        return acc;
      }

      if (!formSchema._exclusive?.required) {
        try {
          const context = formRef.current;

          await schema.current.validateAt(prefix, context, { context });
          return [];
        } catch {
          return [prefix];
        }
      }

      return [prefix];
    },
    [formRef],
  );

  const resetDeprecatedErrors = useCallback(async () => {
    const fieldsWithPotentialErrors = await getFieldsWithPotentialErrors();

    setValidationErrors(prev => _.pick(prev, fieldsWithPotentialErrors));
  }, [getFieldsWithPotentialErrors]);

  const validate = useCallback(async (form, options) => {
    if (!schema.current) {
      logger.error('Validation error: No schema provided');
      return false;
    }

    try {
      setValidationErrors({});
      validationErrorsRef.current = {};
      await schema.current.validate(form, { abortEarly: false, ...options });

      return true;
    } catch (err) {
      const excluded = excludedValidationPathsRef.current;

      const errors =
        err.inner?.reduce((acc, { path, message }) => {
          if (_.get(acc, path)) {
            return acc;
          }

          if (excluded.size && excluded.has(normalizeFormPath(path))) {
            return acc;
          }

          return _.set(acc, path, message);
        }, {}) ?? {};

      setValidationErrors(errors);
      validationErrorsRef.current = errors;

      return _.isEmpty(errors);
    }
  }, []);

  const staticValidateField = useCallback(
    async (path, fieldValue, context, { isDeferredUntilSchema = false } = {}) => {
      if (!schema.current) {
        if (isDeferredUntilSchema) {
          pendingValidationRef.current = (pendingValidationRef.current ?? new Map()).set(path, {
            fieldValue,
            context,
          });

          return;
        }

        logger.error('Validation error: No schema provided');
        return;
      }

      if (excludedValidationPathsRef.current.has(normalizeFormPath(path))) {
        return;
      }

      try {
        setValidationErrors(prevErrors => _.omit(prevErrors, path));

        const validationContext = _.set(
          _.cloneDeep({ ...context, ...formRef.current }),
          path,
          fieldValue,
        );

        await schema.current.validateAt(path, validationContext, { context: validationContext });
      } catch (err) {
        setValidationErrors(prevErrors => _.set(_.cloneDeep(prevErrors), path, err.message));

        return false;
      }
    },
    [formRef],
  );

  const flushPendingValidation = useCallback(
    async pendingFields =>
      Array.from(pendingFields.entries()).reduce(
        (chain, [path, { fieldValue, context }]) =>
          chain.then(() =>
            staticValidateField(
              path,
              _.has(formRef.current, path) ? _.get(formRef.current, path) : fieldValue,
              context,
              { isDeferredUntilSchema: true },
            ),
          ),
        Promise.resolve(),
      ),
    [formRef, staticValidateField],
  );

  const setSchema = useCallback(
    formSchema => {
      const pendingFields = pendingValidationRef.current;

      schema.current = formSchema;

      if (_.isNil(pendingFields)) {
        resetDeprecatedErrors();

        return;
      }

      pendingValidationRef.current = null;

      resetDeprecatedErrors()
        .then(() => flushPendingValidation(pendingFields))
        .catch(error => logger.error('Validation error: Deferred replay failed', error));
    },
    [resetDeprecatedErrors, flushPendingValidation],
  );

  const reportUnservicedValidationRequests = useCallback(() => {
    const pendingFields = pendingValidationRef.current;

    pendingValidationRef.current = null;

    if (_.isNil(pendingFields)) {
      return;
    }

    logger.error('Validation error: No schema provided', {
      fields: Array.from(pendingFields.keys()),
    });
  }, []);

  const validateField = useMemo(() => _.debounce(staticValidateField, 200), [staticValidateField]);

  useEffect(() => () => validateField.cancel(), [validateField]);

  useEffectSafeStrictMode(
    () => reportUnservicedValidationRequests,
    [reportUnservicedValidationRequests],
  );

  return {
    validationErrors: omittedValidationErrors,
    validationErrorsRef,
    setSchema,
    validate,
    validateField,
    setValidationErrors,
    staticValidateField,
    setExcludedValidationPaths,
  };
};

export default useValidation;
